// Smart-split a full HTML document into { html, css, js, head_extras } so a
// pasted template renders correctly inside the CustomCodeSection iframe.
//
// Rules:
//   - Every <style>…</style> block goes into `css`
//   - Every inline <script>…</script> (no src) goes into `js`
//   - <link>, <meta>, <title>, and <script src=…> from <head> go into
//     `head_extras` so external CSS/fonts/CDN JS keep working
//   - Everything inside <body>…</body> becomes `html`
//   - Falls back gracefully for HTML fragments (no <html>/<body>)

const HEAD_EXTRAS_ALLOWED = /^\s*<(link|meta|title|base|noscript)\b/i;

export function splitHtmlDocument(raw) {
  if (!raw || typeof raw !== "string") return { html: "", css: "", js: "", head_extras: "" };
  let src = raw;

  // 1) Extract <style>...</style>
  const cssParts = [];
  src = src.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, (_m, body) => {
    cssParts.push(body.trim());
    return "";
  });

  // 2) Extract inline <script>...</script>. Keep external scripts (with src) as head/body extras.
  const jsParts = [];
  const externalScripts = [];
  src = src.replace(/<script\b([^>]*)>([\s\S]*?)<\/script>/gi, (m, attrs, body) => {
    if (/\bsrc\s*=/.test(attrs)) {
      externalScripts.push(m);
      return "";
    }
    jsParts.push(body.trim());
    return "";
  });

  // 3) Pull <head> contents (excluding style/script already stripped) into head_extras
  const headExtras = [];
  const headMatch = src.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
  if (headMatch) {
    const headInner = headMatch[1];
    // Split into tags by tag boundaries and keep only allowed ones (link, meta, title, base, noscript)
    const tagRe = /<[a-zA-Z!][^>]*>[\s\S]*?<\/[a-zA-Z]+>|<[a-zA-Z!][^>]*\/?>/g;
    const tags = headInner.match(tagRe) || [];
    for (const t of tags) {
      if (HEAD_EXTRAS_ALLOWED.test(t)) headExtras.push(t.trim());
    }
    src = src.replace(/<head[^>]*>[\s\S]*?<\/head>/i, "");
  }

  // External scripts belong at the end of head (or start of body) so they load before inline JS
  for (const es of externalScripts) headExtras.push(es);

  // 4) Body content
  let html = "";
  const bodyMatch = src.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) {
    html = bodyMatch[1].trim();
  } else {
    html = src
      .replace(/<!doctype[^>]*>/gi, "")
      .replace(/<\/?html[^>]*>/gi, "")
      .replace(/<\/?body[^>]*>/gi, "")
      .trim();
  }

  return {
    html,
    css: cssParts.filter(Boolean).join("\n\n"),
    js: jsParts.filter(Boolean).join("\n\n"),
    head_extras: headExtras.join("\n"),
  };
}

// Rough guess whether a pasted string is a full HTML document (vs a fragment).
export function looksLikeFullHtml(raw) {
  if (!raw) return false;
  return /<html[\s>]/i.test(raw) || /<body[\s>]/i.test(raw) || /<!doctype/i.test(raw);
}
